import { useMemo, useState } from 'react';

import { Folder, Search } from 'iconoir-react';
import { LibraryNavIcon } from 'components/blocks/layout/icons/library-nav-icon';
import { ChevronRight, ListFilter, X } from 'lucide-react';
import { FilterType, HeaderToolbarConfig, LibraryTab } from '../../types/header-toolbar.type';
import { useIsMobile } from 'hooks/use-mobile';
import { useTranslation } from 'react-i18next';
import { countActiveFilters } from '../common-filters';
import { HeaderActions } from './header-actions';
import AddDropdownMenu from '../file-manager-add-new-dropdown';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from 'components/ui/sheet';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { FilterControls } from './filter-controls';
import { ActiveFilters } from './active-filters';
import { ViewToggle } from './view-toggle';
import ConfigurationHeader from 'components/configuration/ConfigurationHeader';
import { useTheme } from 'components/core/theme-provider';
import { useIsMediaLibrary } from 'hooks/use-is-media-library';
import { cn } from 'lib/utils';
import { useGetLibraryFolders } from '../../hooks/use-library-folders';
import { useLibraryCurrentFolder } from '../../hooks/use-library-current-folder';
import { LIBRARY_ROOT_DIRECTORY_ID } from '../../services/library-folders-service';

const searchFieldClassName =
  'flex h-12 w-full min-w-0 items-center gap-3 rounded-lg border border-grey-100 bg-white p-3 dark:border-border dark:bg-card sm:max-w-search';

export const BaseHeaderToolbar = <T extends FilterType>({
  title,
  subtitle,
  viewMode,
  searchQuery,
  filters,
  selectedItems = [],
  showFileUpload = false,
  showFolderCreate = false,
  filterConfigs,
  actions = [],
  onViewModeChange,
  onSearchChange,
  onFiltersChange,
  onFileUpload,
  onFolderCreate,
  showTitle = true,
}: HeaderToolbarConfig<T>) => {
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const { theme } = useTheme();
  const titleColor = theme === 'dark' ? 'text-true-white' : 'text-grey-900';
  const mutedColor = theme === 'dark' ? 'text-grey-300' : 'text-grey-500';
  const [openSheet, setOpenSheet] = useState(false);
  const isMediaLibrary = useIsMediaLibrary();
  const { folderId, isRoot, openFolder } = useLibraryCurrentFolder();
  const { data: libraryFolders = [] } = useGetLibraryFolders(isMediaLibrary);

  const handleSearchChange = (query: string) => {
    onSearchChange(query);
    onFiltersChange({
      ...filters,
      name: query,
    } as T);
  };

  const handleResetFilters = () => {
    const resetFilters = Object.keys(filters).reduce((acc, key) => {
      acc[key] = key === 'name' ? '' : undefined;
      return acc;
    }, {} as Record<string, unknown>);

    onSearchChange('');
    onFiltersChange(resetFilters as T);
  };

  const activeFiltersCount = countActiveFilters(filters);
  const hasActiveFilters = activeFiltersCount > 0;

  const TitleIcon = isMediaLibrary ? LibraryNavIcon : Folder;

  const headerActions = (
    <div
      className={cn(
        'flex items-center justify-end gap-2 sm:gap-4',
        isMediaLibrary ? 'w-full sm:w-auto' : 'shrink-0'
      )}
    >
      {!isMediaLibrary && (
        <ViewToggle viewMode={viewMode} onViewModeChange={onViewModeChange} className="h-8" />
      )}
      <HeaderActions actions={actions} selectedItems={selectedItems} isMobile={isMobile} />
      {(showFileUpload || showFolderCreate) && (
        <AddDropdownMenu
          isMobile={isMobile}
          onFileUpload={onFileUpload}
          onFolderCreate={onFolderCreate}
        />
      )}
    </div>
  );

  const folderPath = useMemo(() => {
    if (!isMediaLibrary || isRoot) return [];
    const byId = new Map(libraryFolders.map((folder) => [folder.id, folder]));
    const path: typeof libraryFolders = [];
    let current = byId.get(folderId);
    const seen = new Set<string>();
    while (current && !seen.has(current.id)) {
      seen.add(current.id);
      path.unshift(current);
      const parentId = current.parentDirectoryId;
      if (!parentId || parentId === LIBRARY_ROOT_DIRECTORY_ID) break;
      current = byId.get(parentId);
    }
    return path;
  }, [folderId, isMediaLibrary, isRoot, libraryFolders]);

  const libraryTab: LibraryTab =
    (filters as { libraryTab?: LibraryTab }).libraryTab ?? 'all';

  const handleLibraryTabChange = (tab: LibraryTab) => {
    onFiltersChange({
      ...filters,
      libraryTab: tab === 'all' ? undefined : tab,
    } as T);
  };

  const searchField = (
    <div className={searchFieldClassName}>
      <div className="flex h-6 w-6 shrink-0 items-center justify-center">
        <Search width={15} height={15} strokeWidth={1.75} className="text-grey-400" />
      </div>
      <Input
        value={searchQuery}
        onChange={(event) => handleSearchChange(event.target.value)}
        placeholder={t('SEARCH_BY_FILE_NAME')}
        className={cn(
          'h-full min-h-0 flex-1 border-0 bg-transparent p-0 font-sans text-base font-normal leading-heading shadow-none placeholder:text-grey-400 focus-visible:ring-0 md:text-sm dark:bg-transparent',
          titleColor
        )}
      />
      {isMobile ? (
        <Sheet open={openSheet} onOpenChange={setOpenSheet}>
          <SheetTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              className="relative size-10 shrink-0 rounded-full border-grey-100 dark:border-grey-400"
            >
              <ListFilter className={cn('size-4', titleColor)} />
              {activeFiltersCount > 0 ? (
                <span className="absolute -right-1 -top-1 flex size-4 items-center justify-center rounded-full bg-primary text-3xs text-primary-foreground">
                  {activeFiltersCount}
                </span>
              ) : null}
            </Button>
          </SheetTrigger>
          <SheetContent side="bottom" className="w-full">
            <SheetHeader>
              <SheetTitle>{t('FILTERS')}</SheetTitle>
            </SheetHeader>
            <div className="py-4">
              <FilterControls
                filters={filters}
                filterConfigs={filterConfigs}
                onFiltersChange={onFiltersChange}
                isMobile={true}
              />
            </div>
            {hasActiveFilters ? (
              <Button variant="ghost" onClick={handleResetFilters} className="h-8 w-full px-2">
                {t('RESET')}
                <X className="ml-2 size-4" />
              </Button>
            ) : null}
          </SheetContent>
        </Sheet>
      ) : null}
    </div>
  );

  const titleBlock = showTitle ? (
    <header
      className={cn(
        'flex w-full self-stretch',
        isMediaLibrary
          ? 'min-w-0 flex-col gap-4 sm:flex-row sm:items-start sm:justify-between sm:gap-4'
          : 'items-center justify-between gap-3'
      )}
    >
      {isMediaLibrary ? (
        <ConfigurationHeader
          title={t(title)}
          subtitle={
            subtitle
              ? t(subtitle, {
                  defaultValue:
                    'Collections of media files and attachments that you have used in previous conversations',
                })
              : undefined
          }
          icon={<LibraryNavIcon width={20} height={20} strokeWidth={1.5} className={titleColor} />}
        />
      ) : (
        <div className="flex h-8 min-w-0 flex-1 items-center gap-2 sm:w-72 sm:flex-none">
          <div className="flex size-8 shrink-0 items-center justify-center">
            <TitleIcon width={25} height={25} strokeWidth={1.75} className={titleColor} />
          </div>
          <h1
            className={cn(
              'min-w-0 max-w-full truncate whitespace-nowrap font-sans text-2xl font-medium not-italic leading-heading tracking-normal',
              titleColor
            )}
          >
            {t(title)}
          </h1>
        </div>
      )}
      {headerActions}
    </header>
  ) : (
    <div className="flex w-full items-center justify-end">{headerActions}</div>
  );

  const libraryTabs = (
    <div className="inline-flex max-w-full min-w-0 shrink-0 flex-nowrap items-center gap-2 overflow-x-auto">
      {(
        [
          { label: t('ALL', { defaultValue: 'All' }), value: 'all' as const },
          { label: t('FILES', { defaultValue: 'Files' }), value: 'files' as const },
          { label: t('FOLDERS', { defaultValue: 'Folders' }), value: 'folders' as const },
        ]
      ).map((tab) => {
        const isActive = libraryTab === tab.value;

        return (
          <button
            key={tab.value}
            type="button"
            aria-pressed={isActive}
            onClick={() => handleLibraryTabChange(tab.value)}
            className={cn(
              'inline-flex h-10 shrink-0 items-center rounded-lg border px-3 text-sm font-normal leading-heading transition-colors',
              isActive
                ? cn(
                    'border-grey-100 bg-grey-50 dark:border-true-white/40 dark:bg-true-white/15',
                    titleColor
                  )
                : cn(
                    'border-transparent hover:bg-grey-50 dark:hover:bg-true-white/10',
                    theme === 'dark'
                      ? 'text-true-white/75 hover:text-true-white'
                      : 'text-grey-500 hover:text-grey-900'
                  )
            )}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );

  const filterRow = !isMobile ? (
    <div
      className={cn(
        'flex items-center',
        isMediaLibrary ? 'h-10 shrink-0 gap-4' : ''
      )}
    >
      <FilterControls
        filters={filters}
        filterConfigs={filterConfigs}
        onFiltersChange={onFiltersChange}
        isMobile={false}
      />
      {isMediaLibrary ? (
        <ViewToggle viewMode={viewMode} onViewModeChange={onViewModeChange} />
      ) : null}
    </div>
  ) : null;

  const activeFilters = (
    <ActiveFilters
      filters={filters}
      filterConfigs={filterConfigs}
      onFiltersChange={onFiltersChange}
      onResetAll={handleResetFilters}
    />
  );

  return (
    <div
      className={cn(
        'flex w-full shrink-0 flex-col items-center pb-4 pt-4',
        !isMediaLibrary && 'px-4 sm:px-6'
      )}
    >
      <div
        className={cn(
          'flex w-full flex-col',
          !isMediaLibrary && 'max-w-chat gap-6 md:gap-8'
        )}
      >
        {isMediaLibrary ? (
          <div className="flex w-full min-w-0 flex-col gap-4 border-b border-grey-100 pb-4 dark:border-border sm:gap-6 sm:pb-6">
            {titleBlock}
            {searchField}
          </div>
        ) : (
          titleBlock
        )}

        <div className={cn('flex w-full flex-col', isMediaLibrary ? 'gap-4 pt-4 sm:gap-6 sm:pt-10' : 'gap-4')}>
          {!isMediaLibrary && searchField}
          {isMediaLibrary && !isRoot ? (
            <nav className="flex flex-wrap items-center gap-1 text-sm" aria-label="Folder path">
              <button
                type="button"
                onClick={() => openFolder(LIBRARY_ROOT_DIRECTORY_ID)}
                className={cn(
                  mutedColor,
                  theme === 'dark' ? 'hover:text-true-white' : 'hover:text-grey-900'
                )}
              >
                {t('MEDIA_LIBRARY')}
              </button>
              {folderPath.map((folder, index) => (
                <span key={folder.id} className="flex items-center gap-1">
                  <ChevronRight className="size-4 text-grey-300" />
                  {index === folderPath.length - 1 ? (
                    <span className={cn('font-medium', titleColor)}>{folder.name}</span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => openFolder(folder.id)}
                      className={cn(
                        mutedColor,
                        theme === 'dark' ? 'hover:text-true-white' : 'hover:text-grey-900'
                      )}
                    >
                      {folder.name}
                    </button>
                  )}
                </span>
              ))}
            </nav>
          ) : null}
          {isMediaLibrary ? (
            <div className="flex w-full flex-wrap items-center justify-between gap-4">
              {libraryTabs}
              {/* Filters move into the bottom sheet on mobile, but the view toggle must stay visible */}
              {isMobile ? (
                <ViewToggle viewMode={viewMode} onViewModeChange={onViewModeChange} />
              ) : (
                filterRow
              )}
            </div>
          ) : (
            filterRow
          )}
          {activeFilters}
        </div>
      </div>
    </div>
  );
};
